import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { CheckCircle2, Loader2, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { usePromoteMapping } from "~/lib/feeds";

interface Props {
    slug: string;
    disabled?: boolean;
    /** Called after a successful promote. */
    onPromoted?: (version: number) => void;
    triggerLabel?: string;
    triggerClassName?: string;
}

export function PromoteMappingDialog({
    slug,
    disabled,
    onPromoted,
    triggerLabel = "Promote to active",
    triggerClassName = "btn btn--primary press-crush",
}: Props): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const [error, setError] = useState<string | null>(null);
    const promote = usePromoteMapping(slug);

    async function handlePromote(): Promise<void> {
        setError(null);
        try {
            const res = await promote.mutateAsync(note.trim() || undefined);
            toast.success(`Mapping v${res.mappingVersion} now active`);
            setOpen(false);
            setNote("");
            onPromoted?.(res.mappingVersion);
        } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setError("Promote requires role `admin` or `feed_publisher`.");
            } else {
                const msg = err instanceof ApiError ? err.message : "Failed to promote mapping.";
                setError(msg);
            }
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
                <button type="button" className={triggerClassName} disabled={disabled}>
                    <CheckCircle2 size={14} />
                    <span>{triggerLabel}</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content
                        className="dialog-content enter-up"
                        style={{ padding: "var(--space-6)", maxWidth: 480, width: "min(480px, 100vw)" }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Promote mapping</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <Dialog.Description asChild>
                            <div className="stack-sm" style={{ marginTop: "var(--space-4)" }}>
                                <p className="text-13 text-text-muted">
                                    Promotes the current draft to the active mapping for <code className="type-mono-sm">{slug}</code>.
                                    Ingest runs starting after this point use the new shape. A history
                                    entry is recorded with your user ID.
                                </p>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Note (optional)</span>
                                    <input
                                        className="input"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="Added killchain_phase fallback"
                                    />
                                </label>
                                {error && (
                                    <div
                                        className="chip chip--danger shake"
                                        style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                        role="alert"
                                    >
                                        <ShieldAlert size={12} style={{ marginRight: 6 }} />
                                        {error}
                                    </div>
                                )}
                                <div className="cluster-xs" style={{ justifyContent: "flex-end", marginTop: "var(--space-2)" }}>
                                    <Dialog.Close asChild>
                                        <button type="button" className="btn btn--ghost">Cancel</button>
                                    </Dialog.Close>
                                    <button
                                        type="button"
                                        className="btn btn--primary press-crush"
                                        disabled={promote.isPending}
                                        onClick={() => void handlePromote()}
                                        autoFocus
                                    >
                                        {promote.isPending ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <CheckCircle2 size={14} />
                                        )}
                                        <span>{promote.isPending ? "Promoting…" : "Promote"}</span>
                                    </button>
                                </div>
                            </div>
                        </Dialog.Description>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
